import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  platformSettings,
  type PlatformTenantSettings,
  type PlatformTenantSettingsInput,
} from '@/shared/api/resources';
import { ajudaDaConfiguracaoDePlataforma as ajuda } from '@/shared/help/ajuda/platform';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { formatBytes } from '../documents/envio';
import { fieldErrorOf } from '../shared/fieldError';

import { indentarJson } from './jsonDoPortal';

/**
 * Configuracao de plataforma da empresa, no /admin (P-PLAT).
 *
 * O dono do produto decidiu, em 24/10/2026, que entram todos os campos de plataforma
 * da empresa do OnPoint: limite de sinistros por mes, cota de disco, API externa de
 * envio de sinistros, empresa nova e o ambiente do portal em JSON. Quem os escreve e
 * a administracao da plataforma, e nao o administrador da empresa. So a cota de disco
 * tem efeito: o envio de documento confere o espaco usado. Os demais sao gravados e
 * mostrados, sem efeito, ate a regra ter fonte. O servidor confere cada pedido.
 */
export function PlatformSettingsSection({
  tenantId,
  encerrada,
}: {
  tenantId: string;
  encerrada: boolean;
}) {
  const consulta = useQuery({
    queryKey: ['platform', 'settings', tenantId],
    queryFn: ({ signal }) => platformSettings.get(tenantId, signal),
  });

  return (
    <Card>
      <CardHeader
        title="Configuracao de plataforma"
        description="Limites e marcas da empresa que so a administracao da plataforma altera. Cada alteracao fica na trilha da plataforma e na trilha da empresa, com o antes e o depois."
      />
      {consulta.isPending ? (
        <CardBody>
          <p className="text-sm text-ink-500">Carregando a configuracao da empresa.</p>
        </CardBody>
      ) : consulta.isError ? (
        <CardBody>
          <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
            {consulta.error.message}
          </Alert>
        </CardBody>
      ) : (
        // A chave refaz o formulario quando o servidor devolve outro estado, para que
        // a tela nunca mostre um valor que ja nao vale.
        <FormularioDaConfiguracao
          key={consulta.data.updated_at ?? 'padrao'}
          tenantId={tenantId}
          atual={consulta.data}
          encerrada={encerrada}
        />
      )}
    </Card>
  );
}

/** Texto do inteiro opcional: vazio e sem limite. */
function textoDoInteiro(valor: number | null): string {
  return valor === null ? '' : String(valor);
}

/** Inteiro sem sinal, ou vazio. Devolve undefined quando o texto nao e inteiro. */
function lerInteiro(texto: string): number | null | undefined {
  const limpo = texto.trim();
  if (limpo === '') return null;
  if (!/^\d{1,9}$/.test(limpo)) return undefined;
  return Number(limpo);
}

/**
 * Le o JSON do portal. Devolve o texto, que e o que vai ao servidor, ou undefined e o
 * motivo quando nao e um objeto JSON.
 *
 * O `JSON.parse` serve so para conferir: o valor enviado e o proprio texto, para que o
 * servidor grave exatamente o que a pessoa escreveu (R5-M23).
 */
function lerAmbiente(texto: string): {
  valor: string | null | undefined;
  erro?: string;
} {
  const limpo = texto.trim();
  if (limpo === '') return { valor: null };
  try {
    const lido: unknown = JSON.parse(limpo);
    if (lido === null || typeof lido !== 'object' || Array.isArray(lido)) {
      return {
        valor: undefined,
        erro: 'O ambiente do portal precisa ser um objeto JSON, entre chaves.',
      };
    }
    return { valor: limpo };
  } catch {
    return {
      valor: undefined,
      erro: 'O ambiente do portal precisa ser um JSON valido.',
    };
  }
}

function FormularioDaConfiguracao({
  tenantId,
  atual,
  encerrada,
}: {
  tenantId: string;
  atual: PlatformTenantSettings;
  encerrada: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [sinistros, setSinistros] = useState(
    textoDoInteiro(atual.limit_claims_per_month),
  );
  const [excederSinistros, setExcederSinistros] = useState(
    atual.allow_exceed_claims_limit,
  );
  const [disco, setDisco] = useState(textoDoInteiro(atual.limit_disk_space_gb));
  const [excederDisco, setExcederDisco] = useState(atual.allow_exceed_disk_space_limit);
  const [apiExterna, setApiExterna] = useState(atual.has_external_claim_upload_api);
  const [nova, setNova] = useState(atual.is_new);
  // O texto gravado, so com espaco acrescentado para leitura: nenhum valor muda.
  const [ambiente, setAmbiente] = useState(() =>
    atual.portal_environment_text
      ? indentarJson(atual.portal_environment_text)
      : atual.portal_environment
        ? JSON.stringify(atual.portal_environment, null, 2)
        : '',
  );

  const sinistrosLidos = lerInteiro(sinistros);
  const discoLido = lerInteiro(disco);
  const ambienteLido = lerAmbiente(ambiente);
  const erroDeSinistros =
    sinistrosLidos === undefined
      ? 'Informe um numero inteiro, ou deixe vazio.'
      : undefined;
  const erroDeDisco =
    discoLido === undefined || discoLido === 0
      ? 'Informe um numero inteiro de gigabytes, a partir de 1, ou deixe vazio para sem cota.'
      : undefined;
  const invalido = Boolean(erroDeSinistros || erroDeDisco || ambienteLido.erro);

  const salvar = useMutation({
    mutationFn: () => {
      const entrada: PlatformTenantSettingsInput = {
        limit_claims_per_month: sinistrosLidos ?? null,
        allow_exceed_claims_limit: excederSinistros,
        limit_disk_space_gb: discoLido ?? null,
        allow_exceed_disk_space_limit: excederDisco,
        has_external_claim_upload_api: apiExterna,
        is_new: nova,
        portal_environment: ambienteLido.valor ?? null,
      };
      return platformSettings.update(tenantId, entrada);
    },
    onSuccess: (gravada) => {
      queryClient.setQueryData(['platform', 'settings', tenantId], gravada);
      void queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      toast.success('Configuracao de plataforma atualizada.');
    },
  });

  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (invalido) return;
    salvar.mutate();
  }

  return (
    <form onSubmit={enviar}>
      <CardBody className="flex flex-col gap-5">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar a configuracao">
            {salvar.error.message}
          </Alert>
        ) : null}

        <p className="text-2xs text-ink-500">
          {atual.updated_at
            ? `Alterada por ${atual.updated_label || 'administracao da plataforma'} em ${formatDateTime(atual.updated_at)}.`
            : 'Ainda no padrao: nenhum limite, nada ligado e sem JSON do portal.'}
        </p>

        <fieldset
          disabled={encerrada || salvar.isPending}
          className="flex flex-col gap-5"
        >
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-ink-900">Cota de disco</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Cota de disco (GB)"
                inputMode="numeric"
                placeholder="Sem cota"
                value={disco}
                help={ajuda['Cota de disco (GB)']}
                error={erroDeDisco ?? fieldErrorOf(apiError, 'limit_disk_space_gb')}
                onChange={(evento) => setDisco(evento.target.value)}
              />
              <div className="flex flex-col justify-end gap-1 text-sm text-ink-700">
                <span>
                  Em uso: {formatBytes(atual.disk_used_bytes)}
                  {atual.disk_limit_bytes !== null
                    ? ` de ${formatBytes(atual.disk_limit_bytes)}`
                    : ', sem cota'}
                </span>
                {atual.disk_over_limit ? (
                  <Badge tone="danger">Acima da cota</Badge>
                ) : null}
              </div>
            </div>
            <Interruptor
              rotulo="Permite exceder a cota de disco"
              ligado={excederDisco}
              aoMudar={setExcederDisco}
              explicacao="Ligado, o envio de documento que passa da cota segue com aviso. Desligado, que e o padrao, o envio que passaria da cota e recusado."
            />
          </section>

          <section className="flex flex-col gap-2 border-t border-line pt-4">
            <h3 className="text-sm font-semibold text-ink-900">Sinistros</h3>
            <Alert tone="info" title="Gravado, sem efeito">
              Nenhuma fonte diz o que o limite conta nem o que a API externa faz, e por
              isso nada e bloqueado ou enviado por eles ate a regra ser confirmada.
            </Alert>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Limite de sinistros por mes"
                inputMode="numeric"
                placeholder="Sem limite"
                value={sinistros}
                help={ajuda['Limite de sinistros por mes']}
                error={
                  erroDeSinistros ?? fieldErrorOf(apiError, 'limit_claims_per_month')
                }
                onChange={(evento) => setSinistros(evento.target.value)}
              />
            </div>
            <Interruptor
              rotulo="Permite exceder o limite de sinistros"
              ligado={excederSinistros}
              aoMudar={setExcederSinistros}
              explicacao="So e gravado."
            />
            <Interruptor
              rotulo="API externa de envio de sinistros"
              ligado={apiExterna}
              aoMudar={setApiExterna}
              explicacao="So e gravado: o SeguraCore nao tem essa API."
            />
          </section>

          <section className="flex flex-col gap-2 border-t border-line pt-4">
            <h3 className="text-sm font-semibold text-ink-900">Empresa e portal</h3>
            <Interruptor
              rotulo="Empresa nova"
              ligado={nova}
              aoMudar={setNova}
              explicacao="So e gravado: o significado ainda nao tem fonte."
            />
            <Textarea
              label="Ambiente do portal (JSON)"
              rows={6}
              className="font-mono text-xs"
              placeholder='{ "chave": "valor" }'
              value={ambiente}
              help={ajuda['Ambiente do portal (JSON)']}
              error={ambienteLido.erro ?? fieldErrorOf(apiError, 'portal_environment')}
              onChange={(evento) => setAmbiente(evento.target.value)}
            />
          </section>
        </fieldset>

        {encerrada ? null : (
          <div className="flex justify-end border-t border-line pt-4">
            <Button type="submit" loading={salvar.isPending} disabled={invalido}>
              Salvar configuracao
            </Button>
          </div>
        )}
      </CardBody>
    </form>
  );
}

function Interruptor({
  rotulo,
  ligado,
  aoMudar,
  explicacao,
}: {
  rotulo: string;
  ligado: boolean;
  aoMudar: (valor: boolean) => void;
  explicacao: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-700">
        <input
          type="checkbox"
          role="switch"
          aria-checked={ligado}
          className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
          checked={ligado}
          onChange={(evento) => aoMudar(evento.target.checked)}
        />
        <span>{rotulo}</span>
      </label>
      <p className="pl-6 text-2xs text-ink-500">{explicacao}</p>
    </div>
  );
}
